import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

const RakutenEventPageList = () => {
  const navigate = useNavigate();
  // const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const [searchParams] = useSearchParams();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [crudMode, setCrudMode] = useState('insert');
  const [selectedUuid, setSelectedUuid] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    memo: '',
    template_uuid: '',
  });

  // 🔃 ドラッグ＆ドロップ用のState
  const [dragIndex, setDragIndex] = useState(null);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const pageRes = await fetch(`${API_URL}/rakuten-event/pages/?start=0&length=100`);
      const pageData = await pageRes.json();
      setPages(pageData.data || []);

      const tempRes = await fetch(`${API_URL}/rakuten-event/templates`);
      const tempData = await tempRes.json();
      const safeTemplates = Array.isArray(tempData) ? tempData : (tempData.data || []);
      setTemplates(safeTemplates);

      if (safeTemplates.length > 0 && !formData.template_uuid) {
        setFormData(prev => ({ ...prev, template_uuid: safeTemplates[0].uuid }));
      }
    } catch (error) {
      console.error("データの取得に失敗しました:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      alert("ファイル名を入力してください。");
      return;
    }

    try {
      const url = crudMode === 'insert'
        ? `${API_URL}/rakuten-event/pages/`
        : `${API_URL}/rakuten-event/pages/${selectedUuid}`;

      const method = crudMode === 'insert' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setShowModal(false);
        fetchInitialData();
        setFormData({ name: '', memo: '', template_uuid: templates[0]?.uuid || '' });
      } else {
        alert("保存に失敗しました。");
      }
    } catch (error) {
      console.error("保存エラー:", error);
    }
  };

  const handleDelete = async (uuid) => {
    if (!window.confirm("このファイルを削除してよろしいですか？（大ブロック・中ブロックも連動して削除されます）")) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/rakuten-event/pages/${uuid}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchInitialData();
      } else {
        alert("削除に失敗しました。");
      }
    } catch (error) {
      console.error("削除エラー:", error);
    }
  };

  const openEditModal = (page) => {
    setCrudMode('update');
    setSelectedUuid(page.uuid);
    setFormData({
      name: page.name,
      memo: page.memo || '',
      template_uuid: page.template_uuid
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setCrudMode('insert');
    setSelectedUuid('');
    setFormData({
      name: '',
      memo: '',
      template_uuid: templates[0]?.uuid || ''
    });
    setShowModal(true);
  };

  // 🔃 並べ替え用のアクション
  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragEnter = (index) => {
    if (dragIndex === index) return;
    const newPages = [...pages];
    const draggedItem = newPages[dragIndex];
    newPages.splice(dragIndex, 1);
    newPages.splice(index, 0, draggedItem);

    setDragIndex(index);
    setPages(newPages);
  };

  const handleDragEnd = async () => {
    setDragIndex(null);
    const sortData = pages.map((page, index) => ({
      uuid: page.uuid,
      serial_num: index + 1
    }));

    try {
      const res = await fetch(`${API_URL}/rakuten-event/pages/sort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sortData)
      });

      if (!res.ok) {
        alert("並べ替えの保存に失敗しました。");
        fetchInitialData();
      }
    } catch (error) {
      console.error("並べ替え保存エラー:", error);
      fetchInitialData();
    }
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/">Home</Link></li>
        <li className="breadcrumb-item active">楽天イベントページ一覧</li>
      </ol>
      <h1 className="page-header">楽天イベントページ管理</h1>

      <div className="panel panel-inverse">
        <div className="panel-heading">
          <h4 className="panel-title">ページ一覧</h4>
          {/* 🌟 リロードボタンを追加 */}
          <div className="panel-heading-btn">
            <button
              className="btn btn-xs btn-icon btn-circle btn-success"
              onClick={fetchInitialData}
              title="一覧をリロード"
            >
              <i className="fa fa-redo"></i>
            </button>
          </div>
        </div>

        <div className="panel-toolbar text-end">
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>
            <i className="fa fa-plus me-1"></i> 新規作成
          </button>
        </div>

        <div className="panel-body">
          {loading ? (
            <div className="text-center p-5"><i className="fa fa-spinner fa-spin fa-2x"></i></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th width="1%" className="text-center text-nowrap"><i className="fa fa-sort"></i></th>
                    <th width="30%">Name</th>
                    <th width="30%">Memo</th>
                    <th width="35%">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((page, index) => (
                    <tr
                      key={page.uuid}
                      style={{ cursor: 'grab' }}
                      draggable="true"
                      onDragStart={() => handleDragStart(index)}
                      onDragEnter={() => handleDragEnter(index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      {/* 🌟 ドラッグ用のハンバーガーアイコン */}
                      <td className="text-center text-muted align-middle">
                        <i className="fa fa-bars"></i>
                      </td>
                      <td className="align-middle"><strong>{page.name}</strong></td>
                      <td className="align-middle">{page.memo || <span className="text-muted">-</span>}</td>
                      <td className="align-middle">
                        <a href={`https://www.rakuten.ne.jp/gold/twinklegirls/dist/event/${page.name}.html`}
                          target="_blank" rel="noreferrer" className="btn btn-xs btn-white me-1">
                          楽天のページ
                        </a>
                        <button className="btn btn-xs btn-info me-1" onClick={() => navigate(`/rakuten-event/workspace?uuid=${page.uuid}`)}>
                          移動
                        </button>
                        <button className="btn btn-xs btn-success me-1" onClick={() => openEditModal(page)}>
                          属性編集
                        </button>
                        <button className="btn btn-xs btn-danger" onClick={() => handleDelete(page.uuid)}>
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                  {pages.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-muted p-4">データがありません。新規作成してください。</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* モーダル部分（変更なし） */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {crudMode === 'insert' ? 'ファイルの新規作成' : 'ファイル名の編集'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group row mb-15px">
                    <label className="col-form-label col-md-3">ファイル名</label>
                    <div className="col-md-9">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="例: event2024"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group row mb-15px">
                    <label className="col-form-label col-md-3">メモ名</label>
                    <div className="col-md-9">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="例: お買い物マラソン用"
                        value={formData.memo}
                        onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                      />
                    </div>
                  </div>

                  {crudMode === 'insert' && (
                    <div className="form-group row mb-15px">
                      <label className="col-form-label col-md-3">テンプレート</label>
                      <div className="col-md-9">
                        <select
                          className="form-control"
                          value={formData.template_uuid}
                          onChange={(e) => setFormData({ ...formData, template_uuid: e.target.value })}
                          required
                        >
                          {templates?.map(temp => (
                            <option key={temp.uuid} value={temp.uuid}>{temp.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>閉じる</button>
                  <button type="submit" className="btn btn-primary">保存</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RakutenEventPageList;